import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Heart,
  Calendar,
  User,
  ExternalLink,
  Building2,
  MoreHorizontal,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { formatDate } from "@/utils/dateUtils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function TestsList({
  tests,
  isLoading,
  onEditTest,
  onDeleteTest,
  isPatientView = false,
}) {
  const [expandedTests, setExpandedTests] = useState(new Set());

  const toggleExpanded = (testId) => {
    const newExpanded = new Set(expandedTests);
    if (newExpanded.has(testId)) {
      newExpanded.delete(testId);
    } else {
      newExpanded.add(testId);
    }
    setExpandedTests(newExpanded);
  };

  console.log("tests", { tests });
  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array(3)
          .fill(0)
          .map((_, i) => (
            <Card
              key={i}
              className="bg-white/60 backdrop-blur-sm border-slate-200/60"
            >
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-32" />
                  </div>
                  <Skeleton className="h-6 w-24 rounded-full" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    );
  }

  if (tests.length === 0) {
    return (
      <Card className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-xl">
        <CardContent className="p-12 text-center">
          <Heart className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            No blood tests found
          </h3>
          <p className="text-slate-600 mb-4">
            {isPatientView
              ? "No blood test results are available yet. Contact your doctor for test requests."
              : "Start by uploading your first blood test PDF or adding results manually"}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">
          Test Results ({tests.length})
        </h2>
      </div>

      <div className="space-y-4">
        {tests.map((test) => (
          <Card
            key={test.id}
            className="bg-white/60 backdrop-blur-sm border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-300"
          >
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-100 to-pink-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">
                      {!isPatientView &&
                        `Patient: ${test.patientName || test.patientIdentificationNumber || test.patientEmail}`}
                    </h3>
                    {!isPatientView &&
                      test.patientIdentificationNumber &&
                      test.patientEmail && (
                        <p className="text-xs text-slate-500">
                          {test.patientEmail}
                        </p>
                      )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {test.pdfUrl && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(test.pdfUrl, "_blank")}
                      className="hover:bg-blue-50"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View PDF
                    </Button>
                  )}
                  {!isPatientView && onEditTest && onDeleteTest && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEditTest(test)}>
                          <Edit className="mr-2 h-4 w-4" />
                          <span>Edit</span>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => onDeleteTest(test.id)}
                          className="text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(test.testDate, "MMM d, yyyy")}</span>
                </div>

                {test.labName && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Building2 className="w-4 h-4" />
                    <span>{test.labName}</span>
                  </div>
                )}

                {test.doctorName && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <User className="w-4 h-4" />
                    <span>Dr. {test.doctorName}</span>
                  </div>
                )}

                <div className="text-sm text-slate-500">
                  Added {formatDate(test.createdAt, "MMM d, yyyy")}
                </div>
              </div>

              {test.results && Object.keys(test.results).length > 0 && (
                <div className="mt-4 p-4 bg-slate-50/60 rounded-xl border border-slate-200/60">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-slate-900">
                      Results ({Object.keys(test.results).length})
                    </h4>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(test.id)}
                      className="h-8 w-8 p-0"
                    >
                      {expandedTests.has(test.id) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  {expandedTests.has(test.id) && (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                      {Object.entries(test.results)
                        .filter(([, value]) => value != null)
                        .map(([key, value]) => (
                          <div key={key} className="text-sm">
                            <div className="text-slate-500 capitalize">
                              {key.replace(/_/g, " ")}
                            </div>
                            <div className="font-medium text-slate-900">
                              {typeof value === "number"
                                ? value.toFixed(1)
                                : value}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}

              {test.notes && (
                <div className="mt-4 p-3 bg-blue-50/60 rounded-lg border border-blue-200/60">
                  <p className="text-sm text-slate-700 whitespace-pre-line">
                    {test.notes}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
